/**
 * A line's sentence, cut into the pieces the Team page draws (ADR-0044 §"How
 * it looks on screen").
 *
 * The backend renders the same template into a string and THROWS when a
 * required slot is empty (`workers/jobCatalog.renderSentence`). That is right
 * for a log line and wrong here: a manager reading the Team page is reading it
 * precisely to see which blanks are still empty, so an empty slot is a dotted
 * blank rather than an error. Same template, same binding rules, one different
 * answer at the one place the two surfaces genuinely differ.
 *
 * Copy rule that shows up in the code: the configured line has no noun. It is
 * a sentence with the filter and the trigger filled in, so nothing here ever
 * prints the kind id or the word "job".
 */

import {
  DEFAULT_JOB_INSTANCE,
  qaVerifyRevisionOf,
  type Job,
  type JobKindWire,
  type JobSentenceSegment,
  type JobSentenceSlot,
  type JobSlotPicker,
  type JobTrigger,
  type Schedule,
  type ScheduleDay,
} from "@yagni/shared";

import { scheduleSentence } from "../workers/scheduleSentence.js";

/** The slot pattern the catalog writes: `{name}`, camelCase. */
const SLOT_PATTERN = /\{(\w+)\}/g;

/**
 * A `[…]` fragment: the drop scope inside one clause.
 *
 * It exists so a narrowing can sit in the middle of prose and still
 * disappear when the line does not narrow: "reviews every PR on {repos}[
 * touching {paths}]" is one sentence either way. The clause is the coarse
 * scope; this is the fine one. Brackets never nest.
 */
const FRAGMENT_PATTERN = /\[([^\]]*)\]/g;

/** Clauses inside one sentence are separated by this, as the catalog writes it. */
const CLAUSE_SEPARATOR = " · ";

/** The days a weekday schedule means, for the interval phrasing below. */
const WEEKDAYS: ScheduleDay[] = ["mon", "tue", "wed", "thu", "fri"];

const DAY_WORDS: Record<ScheduleDay, string> = {
  mon: "Monday",
  tue: "Tuesday",
  wed: "Wednesday",
  thu: "Thursday",
  fri: "Friday",
  sat: "Saturday",
  sun: "Sunday",
};

/** "2 hours", "90 minutes", "day": an interval in a manager's words. */
export function formatInterval(minutes: number): string {
  if (minutes % 60 !== 0) return `${minutes} minutes`;
  if (minutes === 24 * 60) return "day";
  const hours = minutes / 60;
  return hours === 1 ? "hour" : `${hours} hours`;
}

function formatDays(days: readonly ScheduleDay[] | undefined): string {
  if (!days || days.length === 0 || days.length === 7) return "day";
  if (days.length === 5 && WEEKDAYS.every((day) => days.includes(day))) return "weekday";
  return days.map((day) => DAY_WORDS[day]).join(", ");
}

/**
 * A clock trigger's schedule as the LINE reads it.
 *
 * Deliberately not `scheduleSentence` from the Rhythm editor: that one prints a
 * standalone sentence ("On weekdays, every 2 hours from 8:00 to 18:00") and
 * this one fills a blank inside a longer sentence ("proposes from Jira every
 * _weekday at 09:00_"). Same facts, different grammatical position. The editor
 * keeps its own phrasing where it renders its own preview.
 */
export function formatScheduleSlot(schedule: Schedule): string {
  const days = formatDays(schedule.days);
  if (schedule.every?.minutes) {
    const window = schedule.every.between;
    const when = `${formatInterval(schedule.every.minutes)} on ${days === "day" ? "every day" : `${days}s`}`;
    return window ? `${when} from ${window[0]} to ${window[1]}` : when;
  }
  const times = schedule.times ?? [];
  if (times.length > 0) return `${days} at ${times.join(" and ")}`;
  const slots = schedule.editionSlots ?? [];
  if (slots.length > 0) return `${days} ${slots.join(" and ")}`;
  // A schedule with no times and no interval says nothing, and rendering
  // "every day" from it would put a promise on the line that no clock keeps.
  return "";
}

/** Read a dotted path out of `{trigger, filter, instance, body}`. */
export function readSlotValue(source: unknown, path: string): unknown {
  let current: unknown = source;
  for (const segment of path.split(".")) {
    if (current === null || current === undefined) return undefined;
    if (Array.isArray(current)) {
      const index = Number(segment);
      current = Number.isInteger(index) ? current[index] : undefined;
      continue;
    }
    if (typeof current !== "object") return undefined;
    current = (current as Record<string, unknown>)[segment];
  }
  return current;
}

/** One slot's value as the sentence prints it. "" means the blank is empty. */
export function formatSlotValue(value: unknown, picker: JobSlotPicker): string {
  if (value === null || value === undefined) return "";
  if (typeof value === "string") return value.trim();
  if (typeof value === "number") {
    return picker === "interval" ? formatInterval(value) : String(value);
  }
  if (typeof value === "boolean") return value ? "yes" : "";
  if (Array.isArray(value)) {
    return value.filter((entry) => typeof entry === "string" && entry.trim()).join(", ");
  }
  if (typeof value === "object") {
    const record = value as Record<string, unknown>;
    if (picker === "schedule") return formatScheduleSlot(record as Schedule);
    // A watch-set, or an answerers-by-kind map: the union of its lists.
    const flattened: string[] = [];
    for (const entry of Object.values(record)) {
      if (!Array.isArray(entry)) continue;
      for (const item of entry) {
        if (typeof item === "string" && item.trim()) flattened.push(item.trim());
      }
    }
    return flattened.join(", ");
  }
  return "";
}

/**
 * One slot's value as the SENTENCE prints it. A `choice` blank stores the id
 * the server keeps and reads as the words on the control, so the sentence says
 * "looks for signal worth a person's time", never the id behind it.
 */
export function formatSlotLabel(slot: JobSentenceSlot, value: unknown): string {
  const text = formatSlotValue(value, slot.picker);
  if (slot.picker === "rule") return sentenceExcerpt(text);
  if (slot.picker !== "choice" || !text) return text;
  return slot.choices?.find((choice) => choice.value === text)?.label ?? text;
}

/** How much of a written rule the one-line sentence shows. */
export const SENTENCE_EXCERPT_CHARS = 80;

/**
 * A written rule as the sentence prints it: its opening words on one line,
 * cut at a word and ended "…" past the limit. The full text is the blank's
 * own control on the line page.
 */
export function sentenceExcerpt(text: string): string {
  const flat = text.replace(/\s+/g, " ").trim();
  if (flat.length <= SENTENCE_EXCERPT_CHARS) return flat;
  const cut = flat.slice(0, SENTENCE_EXCERPT_CHARS);
  const space = cut.lastIndexOf(" ");
  return `${(space > SENTENCE_EXCERPT_CHARS / 2 ? cut.slice(0, space) : cut).replace(/[\s,.;:—–-]+$/, "")}…`;
}

/**
 * What a sentence is rendered against: the line's own trigger and filter, plus
 * the engine body a `chronicler.custom` line carries (ADR-0044 decision 2).
 * A kind with no body renders no body blanks, so the key is simply absent.
 */
export interface SentenceSource {
  trigger: JobTrigger;
  filter: Record<string, unknown>;
  /** The instance name. `default` is reserved and reads as absent. */
  instance?: string;
  body?: Record<string, unknown>;
}

export function sentenceSourceFor(job: Job): SentenceSource {
  const filter = { ...((job.filter ?? {}) as Record<string, unknown>) };
  // A Fletcher line's deploy name reads as empty when the backend matches it
  // (`qaVerifyRevisionOf`): the line verifies the repository's environment,
  // so the sentence says that rather than "staging".
  if (job.kind === "qa.verify_pr" && qaVerifyRevisionOf(filter) === null) delete filter.environment;
  return {
    trigger: job.trigger,
    filter,
    ...(job.instance && job.instance !== DEFAULT_JOB_INSTANCE
      ? { instance: job.instance }
      : {}),
    ...(job.body ? { body: { ...job.body } as Record<string, unknown> } : {}),
  };
}

export interface SentenceOptions {
  /** The Agent's name, for the `{agent}` slot. */
  agentName: string;
  /**
   * Drop the leading `{agent}` slot, for a surface that prints the Agent's name
   * itself (the Team page's row sets it in bold ahead of the sentence).
   */
  omitAgent?: boolean;
  /**
   * What the Team calls a slot's value. A blank that stores a reference (a
   * tracker project's id) would otherwise print the id, and a blank left
   * empty would print the kind's generic words where the Team could name the
   * thing. Called with `undefined` for an empty blank; null means "no name
   * here, print it the usual way".
   */
  nameOf?: (slot: string, value: unknown) => string | null;
}

/** One span of a clause, cut into pieces. `dropped` is an empty optional blank. */
function segmentSpan(
  span: string,
  kind: JobKindWire,
  source: SentenceSource,
  options: SentenceOptions,
): { pieces: JobSentenceSegment[]; dropped: boolean } {
  const pieces: JobSentenceSegment[] = [];
  let cursor = 0;

  for (const match of span.matchAll(SLOT_PATTERN)) {
    const name = match[1]!;
    const at = match.index!;
    if (at > cursor) pieces.push({ type: "text", text: span.slice(cursor, at) });
    cursor = at + match[0].length;

    if (name === "agent") {
      if (!options.omitAgent) pieces.push({ type: "text", text: options.agentName });
      continue;
    }

    const slot: JobSentenceSlot | undefined = kind.slots[name];
    if (!slot) {
      // A template naming a slot the kind never declared is a platform bug,
      // not a blank a person can fill. Print it back rather than vanish it.
      pieces.push({ type: "text", text: match[0] });
      continue;
    }

    const value = readSlotValue(source, slot.bind);
    const text = options.nameOf?.(name, value) ?? formatSlotLabel(slot, value);
    if (text) {
      pieces.push({ type: "slot", name, slot, text, empty: false });
      continue;
    }
    if (slot.emptyLabel) {
      pieces.push({ type: "slot", name, slot, text: slot.emptyLabel, empty: false });
      continue;
    }
    if (slot.optional) return { pieces: [], dropped: true };
    pieces.push({ type: "slot", name, slot, text: "", empty: true });
  }

  if (cursor < span.length) pieces.push({ type: "text", text: span.slice(cursor) });
  return { pieces, dropped: false };
}

/**
 * Cut one kind's sentence into text and blanks for this line.
 *
 * Two drop scopes, both keyed on `optional`. A clause whose only empty slot
 * is `optional` drops whole, which is how "· or: nightly at {nightlyAt}"
 * disappears from a line a deploy triggers. A `[…]` fragment inside a clause
 * drops on its own, which is how "[ touching {paths}]" disappears from a
 * review line that narrows nothing and leaves the sentence around it intact.
 * A required empty slot stays and renders dotted: that is the state the Team
 * page exists to expose.
 */
export function segmentSentence(
  kind: JobKindWire,
  source: SentenceSource,
  options: SentenceOptions,
): JobSentenceSegment[] {
  const out: JobSentenceSegment[] = [];
  const clauses = kind.sentence.split(CLAUSE_SEPARATOR);

  clauses.forEach((clause) => {
    const pieces: JobSentenceSegment[] = [];
    let dropped = false;
    let cursor = 0;

    for (const match of clause.matchAll(FRAGMENT_PATTERN)) {
      const at = match.index!;
      if (at > cursor) {
        const before = segmentSpan(clause.slice(cursor, at), kind, source, options);
        if (before.dropped) {
          dropped = true;
          break;
        }
        pieces.push(...before.pieces);
      }
      cursor = at + match[0].length;
      const inner = segmentSpan(match[1]!, kind, source, options);
      if (!inner.dropped) pieces.push(...inner.pieces);
    }

    if (!dropped && cursor < clause.length) {
      const rest = segmentSpan(clause.slice(cursor), kind, source, options);
      if (rest.dropped) dropped = true;
      else pieces.push(...rest.pieces);
    }

    if (dropped) return;
    if (pieces.length === 0) return;

    if (out.length > 0) out.push({ type: "text", text: CLAUSE_SEPARATOR });
    out.push(...pieces);
  });

  // An omitted `{agent}` leaves a leading space on the first clause.
  const first = out[0];
  if (first?.type === "text") {
    const trimmed = first.text.replace(/^\s+/, "");
    if (trimmed) out[0] = { type: "text", text: trimmed };
    else out.shift();
  }
  return out;
}

/**
 * A blank that answers WHEN, rather than what.
 *
 * Either it writes the trigger itself, or it is the clock said as an interval
 * (`pm.advance` keeps its interval on the filter and the trigger reads it, so
 * the bind alone would file the spine's only control under "what it does").
 */
export function isWhenSlot(slot: JobSentenceSlot): boolean {
  return (
    slot.bind.startsWith("trigger.") ||
    slot.picker === "schedule" ||
    slot.picker === "interval"
  );
}

/** The blanks that say when the line fires. Empty for an event kind. */
export function whenSlots(kind: JobKindWire): Array<[string, JobSentenceSlot]> {
  return Object.entries(kind.slots).filter(([, slot]) => isWhenSlot(slot));
}

/** The blanks that say what the line covers. */
export function whatSlots(kind: JobKindWire): Array<[string, JobSentenceSlot]> {
  return Object.entries(kind.slots).filter(
    ([, slot]) => !isWhenSlot(slot) && slot.more !== true,
  );
}

/** The caps (the spec's dagger slots), the last blanks on a line's page. */
export function limitSlots(kind: JobKindWire): Array<[string, JobSentenceSlot]> {
  return Object.entries(kind.slots).filter(
    ([, slot]) => !isWhenSlot(slot) && slot.more === true && slot.picker !== "checks",
  );
}

/**
 * A number box's text as the blank's value: empty, or a half-typed number
 * ("-", "e") that does not parse yet, reads as empty (the kind's default)
 * rather than as an error.
 */
export function readTypedNumber(text: string): number | undefined {
  if (text.trim() === "") return undefined;
  const typed = Number(text);
  return Number.isNaN(typed) ? undefined : typed;
}

/**
 * A number blank's value outside the whole numbers its `range` allows. An
 * empty blank is fine: it means the kind's default.
 */
export function slotOutOfRange(slot: JobSentenceSlot, value: unknown): boolean {
  if (slot.picker !== "number" || !slot.range || value === undefined || value === null) return false;
  return (
    typeof value !== "number" ||
    !Number.isInteger(value) ||
    value < slot.range.min ||
    value > slot.range.max
  );
}

/**
 * The checks a review line applies. A dagger slot in the catalog (it is not
 * in the sentence), but a roster is not a cap, so it draws as its own
 * Checks section on the line's page rather than as a blank.
 */
export function checksSlots(kind: JobKindWire): Array<[string, JobSentenceSlot]> {
  return Object.entries(kind.slots).filter(([, slot]) => slot.picker === "checks");
}

/**
 * Write a slot's value back onto a draft of `{trigger, filter, instance, body}`.
 * Returns a new object; the caller holds the draft.
 */
export function writeSlotValue<T extends SentenceSource>(
  source: T,
  bind: string,
  value: unknown,
): T {
  const segments = bind.split(".");
  const clone = structuredClone(source) as Record<string, unknown>;
  let cursor: Record<string, unknown> = clone;
  for (let i = 0; i < segments.length - 1; i += 1) {
    const key = segments[i]!;
    const next = cursor[key];
    if (next === null || typeof next !== "object") cursor[key] = {};
    cursor = cursor[key] as Record<string, unknown>;
  }
  const last = segments[segments.length - 1]!;
  if (value === undefined || value === "" || (Array.isArray(value) && value.length === 0)) {
    delete cursor[last];
  } else {
    cursor[last] = value;
  }
  return clone as T;
}

/** The blanks a person types into, kept as typed until the line is saved. */
const FREE_TEXT_PICKERS: ReadonlySet<JobSlotPicker> = new Set(["rule", "lane", "text"]);

/**
 * A draft with its typed blanks trimmed, for the save. The blanks keep what is
 * typed (a trailing space is the next word's start), and the server stores a
 * filter string as sent, so the edges come off here.
 */
export function trimFreeText<T extends SentenceSource>(kind: Pick<JobKindWire, "slots">, source: T): T {
  let out = source;
  for (const slot of Object.values(kind.slots)) {
    if (!FREE_TEXT_PICKERS.has(slot.picker)) continue;
    const value = readSlotValue(out, slot.bind);
    if (typeof value === "string" && value !== value.trim()) {
      out = writeSlotValue(out, slot.bind, value.trim());
    }
  }
  return out;
}

/**
 * A draft whose emptied body blanks say null, for the save. An emptied blank
 * leaves the draft, which clears it from the filter (the server replaces that
 * whole), but the server merges the body field by field and keeps what is
 * stored under a field left out. So an emptied body blank goes out as null.
 */
export function clearEmptiedBodyBlanks<T extends SentenceSource>(kind: Pick<JobKindWire, "slots">, source: T): T {
  if (!source.body) return source;
  let out = source;
  for (const slot of Object.values(kind.slots)) {
    if (!slot.bind.startsWith("body.")) continue;
    if (readSlotValue(out, slot.bind) === undefined) out = writeSlotValue(out, slot.bind, null);
  }
  return out;
}

/** The bind of the blank that chooses what starts a line: its clock, or an event. */
export const TRIGGER_EVENT_BIND = "trigger.event";

/** The line starts when a production deploy lands. */
export function startsOnDeploy(trigger: JobTrigger | undefined): boolean {
  return trigger?.type === "event" && trigger.event === "deploy.production";
}

/**
 * A write to the `trigger.event` blank chooses what starts the line, so it
 * replaces the whole trigger: an event the kind listens for makes an event
 * trigger, and any other answer ("on its rhythm") puts back `clock`.
 */
export function writeTriggerEvent<T extends SentenceSource>(
  source: T,
  kind: Pick<JobKindWire, "listens">,
  value: unknown,
  clock: JobTrigger,
): T {
  const event = kind.listens.find((listened) => listened === value);
  const trigger: JobTrigger = event ? { type: "event", event } : structuredClone(clock);
  return { ...structuredClone(source), trigger };
}

/** The sentence as one plain string, for a title attribute or a test. */
export function sentenceText(segments: readonly JobSentenceSegment[]): string {
  return segments
    .map((segment) => (segment.type === "text" ? segment.text : segment.text))
    .join("")
    .trim();
}

// `scheduleSentence` stays the Rhythm editor's own preview voice; re-exported
// here so a surface that wants the standalone phrasing does not reach across
// into `workers/` for it.
export { scheduleSentence };
